import { AlertTriangle } from "lucide-react";
import { useId, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  awaitsReview,
  choiceBody,
  editorOf,
  multiBody,
  OTHER_CHOICE,
  partsBody,
  textBody,
  valueAt
} from "../model";
import type {
  KeyFact,
  KeyFactActionBody,
  KeyFactEditor,
  KeyFactEditorOption
} from "../types";

const optionText = (option: KeyFactEditorOption, proposed = false) => (
  <span className="min-w-0 text-base text-fg">
    {option.label}
    {option.hint ? (
      <span className="ml-1.5 text-sm text-fg-4">{option.hint}</span>
    ) : null}
    {proposed ? (
      <span className="ml-1.5 text-sm text-info">Proposed</span>
    ) : null}
  </span>
);

const CheckList: FC<{
  id: string;
  options: KeyFactEditorOption[];
  value: string[];
  proposed: readonly string[];
  onChange: (next: string[]) => void;
}> = ({ id, options, value, proposed, onChange }) => (
  <div className="grid grid-cols-1 gap-1 @lg/facts:grid-cols-2">
    {options.map((option, i) => {
      const on = value.includes(option.value);
      return (
        <label
          key={option.value}
          htmlFor={`${id}-${i}`}
          className={cn(
            "flex cursor-pointer items-start gap-2 rounded-md px-1.5 py-1",
            on && "bg-fill-2"
          )}
        >
          <Checkbox
            id={`${id}-${i}`}
            checked={on}
            className="mt-0.5"
            onCheckedChange={(checked) =>
              onChange(
                checked === true
                  ? [...value, option.value]
                  : value.filter((v) => v !== option.value)
              )
            }
          />
          {optionText(option, proposed.includes(option.value))}
        </label>
      );
    })}
  </div>
);

const ChoiceList: FC<{
  id: string;
  editor: KeyFactEditor;
  value: string;
  other: string;
  onChange: (value: string) => void;
  onOther: (text: string) => void;
}> = ({ id, editor, value, other, onChange, onOther }) => {
  const options = editor.options ?? [];
  return (
    <RadioGroup value={value} onValueChange={onChange} className="gap-0.5">
      {options.map((option, i) => (
        <label
          key={option.value}
          htmlFor={`${id}-${i}`}
          className={cn(
            "flex cursor-pointer items-start gap-2 rounded-md px-1.5 py-1",
            value === option.value && "bg-fill-2"
          )}
        >
          <RadioGroupItem
            id={`${id}-${i}`}
            value={option.value}
            className="mt-0.5"
          />
          {optionText(option)}
        </label>
      ))}
      {editor.other ? (
        <label
          htmlFor={`${id}-other`}
          className={cn(
            "flex cursor-pointer items-start gap-2 rounded-md px-1.5 py-1",
            value === OTHER_CHOICE && "bg-fill-2"
          )}
        >
          <RadioGroupItem
            id={`${id}-other`}
            value={OTHER_CHOICE}
            className="mt-0.5"
          />
          <span className="text-base text-fg-2">{editor.other.label}</span>
        </label>
      ) : null}
      {value === OTHER_CHOICE && editor.other ? (
        <div className="pt-1 pl-7.5">
          <Input
            autoFocus
            value={other}
            aria-label={editor.other.label}
            placeholder={editor.other.placeholder}
            onChange={(e) => onOther(e.target.value)}
            className="h-8"
          />
        </div>
      ) : null}
    </RadioGroup>
  );
};

const PartsForm: FC<{
  id: string;
  editor: KeyFactEditor;
  entries: Record<string, string>;
  onChange: (path: string, text: string) => void;
}> = ({ id, editor, entries, onChange }) => (
  <div className="grid grid-cols-1 gap-2 @lg/facts:grid-cols-2">
    {(editor.parts ?? []).map((part) => (
      <div key={part.path} className="flex min-w-0 flex-col gap-1">
        <label htmlFor={`${id}-${part.path}`} className="text-sm text-fg-3">
          {part.label}
        </label>
        <Input
          id={`${id}-${part.path}`}
          type={part.kind === "date" ? "date" : "text"}
          inputMode={part.kind === "number" ? "decimal" : undefined}
          value={entries[part.path] ?? ""}
          placeholder={part.placeholder}
          onChange={(e) => onChange(part.path, e.target.value)}
          className="h-8"
        />
      </div>
    ))}
  </div>
);

const initialText = (fact: KeyFact, editor: KeyFactEditor): string => {
  if (editor.type === "date") {
    const raw = typeof fact.value === "string" ? fact.value : "";
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : "";
  }
  return typeof fact.value === "string" ? fact.value : fact.display || "";
};

/**
 * The control a fact's value takes (`editor.type`): a checklist, pick one,
 * a parts form, a date or text. Submits the action body; a proposal left as
 * it is confirms.
 */
export const FactEditor: FC<{
  fact: KeyFact;
  submitLabel: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Under the controls, left of the buttons (Back to the Computed Value). */
  extra?: ReactNode;
  onSubmit: (body: KeyFactActionBody) => void;
  onCancel: () => void;
}> = ({
  fact,
  submitLabel,
  cancelLabel = "Cancel",
  busy = false,
  extra,
  onSubmit,
  onCancel
}) => {
  const id = useId();
  const editor = editorOf(fact);
  const selected = editor.selected ?? [];
  const [checked, setChecked] = useState<string[]>(() => [...selected]);
  const [choice, setChoice] = useState<string>(() => selected[0] ?? "");
  const [other, setOther] = useState("");
  const [entries, setEntries] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (editor.parts ?? []).map((p) => [p.path, valueAt(fact.value, p.path)])
    )
  );
  const [text, setText] = useState(() => initialText(fact, editor));

  let control: ReactNode;
  let body: KeyFactActionBody | null;
  let label = submitLabel;
  let note: string | null = null;
  switch (editor.type) {
    case "multi":
      control = (
        <CheckList
          id={id}
          options={editor.options ?? []}
          value={checked}
          proposed={awaitsReview(fact) ? selected : []}
          onChange={setChecked}
        />
      );
      body = multiBody(fact, editor, checked);
      if (editor.noun)
        label = `${submitLabel} ${checked.length} ${
          checked.length === 1 ? editor.noun[0] : editor.noun[1]
        }`;
      if (editor.min && checked.length < editor.min)
        note = `${checked.length} selected. ${editor.min_note ?? ""}`.trim();
      break;
    case "choice":
      control = (
        <ChoiceList
          id={id}
          editor={editor}
          value={choice}
          other={other}
          onChange={setChoice}
          onOther={setOther}
        />
      );
      body = choice ? choiceBody(fact, editor, choice, other) : null;
      break;
    case "parts":
      control = (
        <PartsForm
          id={id}
          editor={editor}
          entries={entries}
          onChange={(path, value) =>
            setEntries((all) => ({ ...all, [path]: value }))
          }
        />
      );
      body = partsBody(fact, editor, entries);
      break;
    default:
      control =
        editor.type === "date" ? (
          <Input
            type="date"
            aria-label={fact.label}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-8 w-44"
          />
        ) : editor.long ? (
          <Textarea
            aria-label={fact.label}
            value={text}
            rows={3}
            onChange={(e) => setText(e.target.value)}
          />
        ) : (
          <Input
            aria-label={fact.label}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-8"
          />
        );
      body = textBody(fact, text);
  }

  return (
    <form
      className="@container/facts flex flex-col gap-2.5"
      data-testid="fact-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (body && !busy) onSubmit(body);
      }}
    >
      {control}
      {note ? (
        <p className="flex items-center gap-1.5 text-sm text-warn">
          <AlertTriangle className="size-3 shrink-0" aria-hidden />
          {note}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!body || busy}
        >
          {busy ? <Spinner className="size-3" /> : null}
          {label}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {cancelLabel}
        </Button>
        {extra}
      </div>
    </form>
  );
};
